









































import { Component, Vue, Prop, Provide, Watch, PropSync, Ref } from 'vue-property-decorator';
import { makeReactiveProperty, generateUID } from '@/utils/utils';
import { cloneDeep, isEqualWith } from 'lodash';
import { IRules, ValidationGroup } from './ValidationModule';
import { diffString } from 'json-diff';
import SButton from '../SButton.vue';
import { WatchObjectAsJson } from '@/decorators/watch-object-as-json';
import { isDecimal } from '@/utils/decimal';

@Component({
  components: {
    SButton,
  },
})
export default class SForm extends Vue {
  @Prop(Object) public inputs!: {[key: string]: string};
  private deepCopyInputs: {[key: string]: string} = {};
  @Prop(Object) public rules!: IRules;
  @Prop(Boolean) public withLabels?: boolean;
  @Prop({ type: Boolean, default: true }) public focusFirstEmptyField!: boolean;
  @PropSync('valid', { type: Boolean, default: true }) public validSync!: boolean;
  @PropSync('dirty', { type: Boolean, default: false }) public dirtySync!: boolean;

  @Provide('sForm') public sform = this;

  @Ref() public readonly formRef?: HTMLFormElement;

  public validationGroup: ValidationGroup = new ValidationGroup();
  public reactiveProps = { inputs: {}, validationGroup: this.validationGroup };

  public created() {
    makeReactiveProperty(this, this.reactiveProps, 'inputs', 'inputs');
    makeReactiveProperty(this, this.reactiveProps, 'validationGroup', 'validationGroup');
  }

  public mounted() {
    this.resetDirty();
    if (this.focusFirstEmptyField) {
      // On Product prices, inputs are inside stable tds, for some reason the
      // form is mounted before the stable has rendered. Therefore it didn't
      // find any inputs.
      // This will make the sform wait one cycle for the product price form
      // to render.
      this.$nextTick(() => {
        this.findAndFocusFirstEmptyField();
      });
    }
  }

  private findAndFocusFirstEmptyField(): void {
    function elementVisible(element: HTMLElement): boolean {
      if (window.getComputedStyle(element).display === 'none') return false;

      const parent = element.parentElement;
      if (!parent) return true;
      return elementVisible(parent);
    }

    if(!this.formRef) return;
    const elements: Array<HTMLInputElement | HTMLSelectElement> = Array.from(this.formRef.querySelectorAll('input, select'));
    const firstEmptyElement = elements.find(el => el.value === '' && elementVisible(el));
    if (firstEmptyElement) firstEmptyElement.focus();
  }

  public resetDirty(): void {
    this.deepCopyInputs = cloneDeep(this.inputs);
    this.checkDirty();
  }

  public setDirty(): void {
    this.deepCopyInputs = { forcedDirty: generateUID() };
    this.checkDirty();
  }

  @Watch('validationGroup.valid')
  public isValid(): boolean {
    this.validSync = this.validationGroup.valid;
    return this.validSync;
  }

  @WatchObjectAsJson('inputs')
  private checkDirty(): void {
    this.dirtySync = !isEqualWith(this.inputs, this.deepCopyInputs, (value, other, key) => {
      if (/^\$_/.test(String(key))) return true;
      if (isDecimal(value) && isDecimal(other)) return value.eq(other);
    });
  }

  private resetForm(): void {
    Object.assign(this.inputs, cloneDeep(this.deepCopyInputs));
    this.checkDirty();
  }

  @Prop(Boolean) private withDirtyDiff?: boolean;
  @Prop(Boolean) private withResetButton?: boolean;
  @Prop(String) private resetButtonPortalTo?: string;

  private get dirtyDiff() {
    return diffString(this.deepCopyInputs, this.inputs)
      .replace(/\[[0-9;]*m/g, ''); // Remove ANSI color codes
  }

  private get submit() {
    return this.$listeners.submit || (() => {});
  }
}
